import { useState, useEffect, useRef } from "react";
import { apiRequest } from "@/lib/queryClient";
import { useParams, useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  MapPin,
  Clock,
  Briefcase,
  Calendar,
  Users,
  FileText,
  HelpCircle,
  X,
} from "lucide-react";

interface Application {
  id: number;
  pipeline_stage: string;
  status: string;
  candidate_name: string;
  candidate_email?: string;
  created_at: string;
}

interface Question {
  id: number;
  questionText: string;
  questionType?: string;
  category?: string;
  orderIndex?: number;
  timeLimit?: number;
}

interface JobDetails {
  id: string | number;
  title: string;
  location?: string;
  employment_type?: string;
  work_mode?: string;
  salary_band?: string | null;
  description?: string;
  status?: string;
  created_at: string;
  applications?: Application[];
  applicationCount?: number;
  pendingReview?: number;
}

const STAGE_LABELS: Record<string, string> = {
  applied: "Applied",
  link_sent: "Link Sent",
  in_progress: "In Progress",
  submitted: "Submitted",
  ai_triage: "AI Triage",
  under_review: "Under Review",
  dossier_ready: "Dossier Ready",
  outcome: "Outcome",
  expired: "Expired",
  not_proceeding: "Not Proceeding",
};

const STAGE_COLORS: Record<string, string> = {
  applied: "bg-slate-100 text-slate-700",
  link_sent: "bg-blue-100 text-blue-700",
  in_progress: "bg-indigo-100 text-indigo-700",
  submitted: "bg-amber-100 text-amber-700",
  ai_triage: "bg-purple-100 text-purple-700",
  under_review: "bg-orange-100 text-orange-700",
  dossier_ready: "bg-green-100 text-green-700",
  outcome: "bg-emerald-100 text-emerald-700",
  expired: "bg-red-100 text-red-700",
  not_proceeding: "bg-red-100 text-red-700",
};

export default function JobDetail() {
  const params = useParams<{ jobId: string }>();
  const jobId = params.jobId;
  const [, navigate] = useLocation();

  const [job, setJob] = useState<JobDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"details" | "candidates" | "questions">("candidates");

  const [setupMode, setSetupMode] = useState(false);
  const [setupBannerVisible, setSetupBannerVisible] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const questionsSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("tab") === "candidates") {
      setActiveTab("candidates");
    }
    if (urlParams.get("setup") === "true") {
      setSetupMode(true);
      setSetupBannerVisible(true);
      setActiveTab("questions");
    }
  }, []);

  useEffect(() => {
    if (jobId) {
      fetchJob();
    }
  }, [jobId]);

  useEffect(() => {
    if (setupMode && !loading && questionsSectionRef.current) {
      const timer = setTimeout(() => {
        questionsSectionRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [setupMode, loading]);

  useEffect(() => {
    if (activeTab === "questions" && jobId) {
      fetchQuestions();
    }
  }, [activeTab, jobId]);

  const fetchJob = async () => {
    try {
      setLoading(true);
      const res = await apiRequest("GET", `/api/agent/jobs/${jobId}`);
      if (!res.ok) {
        setError(res.status === 404 ? "Job not found" : "Failed to load job details");
        return;
      }
      const data = await res.json();
      setJob(data);
    } catch {
      setError("Failed to load job details");
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    setQuestionsLoading(true);
    try {
      const res = await apiRequest("GET", `/api/agent/jobs/${jobId}/questions`);
      if (res.ok) {
        const data = await res.json();
        setQuestions(Array.isArray(data) ? data : []);
      }
    } catch {
      // silently fail — questions list just stays empty
    } finally {
      setQuestionsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-4xl mx-auto">
          <Link href="/agent/dashboard?tab=roles">
            <Button variant="ghost" className="gap-2 mb-6" data-testid="button-back-to-roles">
              <ArrowLeft className="w-4 h-4" />
              Back to Roles
            </Button>
          </Link>
          <Card className="p-12 text-center">
            <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Job Not Found</h2>
            <p className="text-muted-foreground mb-6">{error || "This job posting may no longer be available."}</p>
            <Link href="/agent/dashboard?tab=roles">
              <Button data-testid="button-back-roles">Back to Roles</Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  const applications = job.applications || [];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto p-6 md:p-8">
        <Link href="/agent/dashboard?tab=roles">
          <Button variant="ghost" className="gap-2 mb-6" data-testid="button-back-to-roles">
            <ArrowLeft className="w-4 h-4" />
            Back to Roles
          </Button>
        </Link>

        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-1" data-testid="text-job-title">{job.title}</h1>
            <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
              {job.location && (
                <span className="flex items-center gap-1" data-testid="text-location">
                  <MapPin className="w-3.5 h-3.5" />{job.location}
                </span>
              )}
              {job.employment_type && (
                <span className="flex items-center gap-1" data-testid="text-employment-type">
                  <Clock className="w-3.5 h-3.5" />
                  {job.employment_type.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              )}
              {job.work_mode && (
                <span className="flex items-center gap-1" data-testid="text-work-mode">
                  <Briefcase className="w-3.5 h-3.5" />
                  {job.work_mode.replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              )}
              {job.salary_band && (
                <span className="font-medium" data-testid="text-salary">{job.salary_band}</span>
              )}
              <span className="flex items-center gap-1" data-testid="text-created-at">
                <Calendar className="w-3.5 h-3.5" />
                Created {new Date(job.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          <Badge
            className={job.status === "active" || job.status === "published" ? "bg-green-100 text-green-800 border-green-200" : ""}
            data-testid="badge-job-status"
          >
            {job.status === "active" || job.status === "published" ? "Active" : (job.status || "Draft")}
          </Badge>
        </div>

        <div className="flex gap-1 border-b border-border mb-6">
          <button
            onClick={() => setActiveTab("candidates")}
            className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${activeTab === "candidates" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="tab-candidates"
          >
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Candidates
              {applications.length > 0 && (
                <span className="bg-primary/10 text-primary text-xs font-bold px-1.5 py-0.5 rounded-full">{applications.length}</span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("details")}
            className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${activeTab === "details" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="tab-details"
          >
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Job Details
            </span>
          </button>
          <button
            onClick={() => setActiveTab("questions")}
            className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${activeTab === "questions" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="tab-questions"
          >
            <span className="flex items-center gap-2">
              <HelpCircle className="w-4 h-4" />
              Interview Questions
            </span>
          </button>
        </div>

        {activeTab === "candidates" && (
          <div data-testid="panel-candidates">
            {applications.length === 0 ? (
              <Card className="p-12 text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No candidates yet</h3>
                <p className="text-muted-foreground text-sm">
                  Generate an interview link from the Roles page to start receiving candidates.
                </p>
                <Link href="/agent/dashboard?tab=roles">
                  <Button className="mt-4" variant="outline" data-testid="button-go-to-roles">Go to Roles</Button>
                </Link>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="list-candidates">
                {applications.map((app) => (
                  <Card key={app.id} className="p-4 flex items-center justify-between gap-4 hover:border-primary/30 transition-colors">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate" data-testid={`text-candidate-name-${app.id}`}>{app.candidate_name}</p>
                      {app.candidate_email && (
                        <p className="text-xs text-muted-foreground truncate">{app.candidate_email}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Added {new Date(app.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge
                      className={`text-xs flex-shrink-0 ${STAGE_COLORS[app.pipeline_stage] || "bg-slate-100 text-slate-700"}`}
                      data-testid={`badge-stage-${app.id}`}
                    >
                      {STAGE_LABELS[app.pipeline_stage] || app.pipeline_stage}
                    </Badge>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "details" && (
          <Card className="p-6" data-testid="panel-job-details">
            {job.description ? (
              <div>
                <h2 className="text-lg font-semibold mb-3">Role Description</h2>
                <p className="text-muted-foreground whitespace-pre-wrap text-sm leading-relaxed" data-testid="text-job-description">
                  {job.description}
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No description has been added for this role yet.</p>
            )}
          </Card>
        )}

        {activeTab === "questions" && (
          <div id="questions-section" ref={questionsSectionRef} data-testid="panel-questions">
            {setupBannerVisible && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 mb-4" data-testid="banner-setup">
                <HelpCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 flex-1">
                  <span className="font-semibold">You've arrived from JobAdder.</span> Review or update your custom interview questions below, then return to JobAdder and click <span className="font-medium">Vexis Interview</span> again to send the assessment.
                </p>
                <button
                  onClick={() => setSetupBannerVisible(false)}
                  className="text-amber-500 hover:text-amber-700 flex-shrink-0"
                  aria-label="Dismiss"
                  data-testid="button-dismiss-setup-banner"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Interview Questions</h2>
                <Link href={`/agent/dashboard?editQuestions=${jobId}`}>
                  <Button size="sm" data-testid="button-edit-questions">
                    Edit Questions
                  </Button>
                </Link>
              </div>

              {questionsLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-6">
                  <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" />
                  <span className="text-sm">Loading questions...</span>
                </div>
              ) : questions.length === 0 ? (
                <div className="text-center py-8">
                  <HelpCircle className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">No interview questions configured yet.</p>
                  <Link href={`/agent/dashboard?editQuestions=${jobId}`}>
                    <Button size="sm" data-testid="button-add-questions">Add Questions</Button>
                  </Link>
                </div>
              ) : (
                <ol className="space-y-3" data-testid="list-questions">
                  {questions
                    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
                    .map((q, idx) => (
                      <li
                        key={q.id}
                        className="flex gap-3 p-3 rounded-md border border-border bg-muted/30"
                        data-testid={`question-item-${q.id}`}
                      >
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center mt-0.5">
                          {idx + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium leading-snug">{q.questionText}</p>
                          {(q.category || q.timeLimit) && (
                            <div className="flex gap-2 mt-1">
                              {q.category && (
                                <span className="text-xs text-muted-foreground">{q.category}</span>
                              )}
                              {q.timeLimit && (
                                <span className="text-xs text-muted-foreground">· {q.timeLimit}s</span>
                              )}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                </ol>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
